import React, { useMemo, useState, useEffect, useRef } from "react";
import PageSelector from "../PageSelector";

import * as fetchJsonp from 'fetch-jsonp';

const DropDown = ({
                      options,
                      selectedOptionIndex,
                      expand,
                      onExpandAction,
                      onSelectAction
                  }:{
    options:string[],
    selectedOptionIndex:number,
    expand:boolean,
    onExpandAction:() => void,
    onSelectAction:(index:number) => void
}) =>
    <>
        {/* <div
            className=
                "mx-auto h-14 border border-gcc-light-blue-gray rounded pl-4 pr-6 flex justify-between items-center"
            style={{maxWidth: '428px'}}
            onClick={onExpandAction}
        >
            <p>{`${selectedOptionIndex === -1? 'Teams' : options[selectedOptionIndex]}`}</p>
            <img className="w-3 h-6 transform rotate-90" src='/images/arrow-right-orange.svg' alt='' />
        </div> */}
        {
            expand?
                <ul
                    className="mx-auto border border-gcc-light-blue-gray rounded bg-gcc-medium-light-gray"
                    style={{maxWidth: '428px'}}
                >
                    {
                        [...options, 'All'].map((option, index) =>
                            <li
                                className="px-4 py-2 hover:bg-gcc-orange"
                                onClick={() => onSelectAction(index === options.length? -1 : index)}
                                key={`item-${index}`}
                            >
                                {option}
                            </li>
                        )
                    }
                </ul> :
                null
        }
    </>;

// How many items per page
const MAX_DISPLAY_ITEMS = 10;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// "Full_time" -> "Full time"
const formatEmploymentType = (type?:string) => {
    if(!type) return null;
    const text = type.replace(/_/g, ' ').toLowerCase();
    return text.charAt(0).toUpperCase() + text.slice(1);
};

// "Houston, TX" or "Houston, TX +2 more" when the job is posted in several locations
const formatLocation = (job:any) => {
    const place = [job.primary_city, job.primary_state].filter(Boolean).join(', ');
    if(!place) return null;
    const extraLocations = job.google_locations ? job.google_locations.length - 1 : 0;
    return extraLocations > 0 ? `${place} +${extraLocations} more` : place;
};

// "Posted today", "Posted 3 days ago", "Posted 2 weeks ago" or the date once it is over a month old
const formatPostedDate = (openDate?:string) => {
    const posted = openDate ? new Date(openDate) : null;
    if(!posted || isNaN(posted.getTime())) return null;
    const days = Math.floor((Date.now() - posted.getTime()) / MS_PER_DAY);
    if(days < 1) return 'Posted today';
    if(days === 1) return 'Posted yesterday';
    if(days < 14) return `Posted ${days} days ago`;
    if(days < 31) return `Posted ${Math.floor(days / 7)} weeks ago`;
    return `Posted ${posted.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})}`;
};

// The short facts shown under the title, in the order people scan for them
const getJobDetails = (job:any) => [
    job.primary_category,
    formatLocation(job),
    formatEmploymentType(job.employment_type),
    // Onsite is the default so only call out Remote / Hybrid
    job.location_type && job.location_type.toLowerCase() !== 'onsite' ? job.location_type : null
].filter(Boolean) as string[];

const JobListSlice = ({data}:any) => {

    const {items} = data;

    // Raw API response, kept unfiltered so changing the team filter doesn't need a refetch
    const [allJobs, setAllJobs] = useState<any[]>([]);

    const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

    // Bumped by the retry button to re-run the fetch
    const [attempt, setAttempt] = useState(0);

    // Set the page index to 0
    const [pageIndex, setPageIndex] = useState(0);

    const [expandDropDown, setExpandDropDown] = useState(false);

    const [teamIndex, setTeamIndex] = useState(-1);

    const listTopRef = useRef<HTMLDivElement>(null);

    const teamOptions:string[] = useMemo(() => items && items.map(({team_option}:any) => team_option), [items])

    const jobs = useMemo(
        () => teamIndex === -1 ?
            allJobs :
            allJobs.filter(
                ({job}:any) =>
                    ((job.primary_category || '') as string)
                        .toLowerCase()
                        .includes(teamOptions[teamIndex].toLowerCase())
            ),
        [allJobs, teamIndex, teamOptions]
    );

    // Page count follows the filtered list, otherwise filtering leaves empty trailing pages
    const numPages = Math.max(1, Math.ceil(jobs.length / MAX_DISPLAY_ITEMS));

    // We need two computed indexes to slice the array of results
    const startIndex = pageIndex * MAX_DISPLAY_ITEMS;
    const endIndex = startIndex + MAX_DISPLAY_ITEMS;

    // On page load use JSONP to fetch the resullts from the API
    useEffect(() => {
        let cancelled = false;
        setStatus('loading');
        /* @ts-ignore */
        fetchJsonp('https://jobsapi-google.m-cloud.io/api/job/search?pageSize=20&offset=0&companyName=companies%2F8454851f-07b7-4e4c-9b5f-00e0ffbfcb09&query=%22GCC%22%20OR%20%22Global%20Custom%20Commerce%22')
            .then(function(response: any) {
                return response.json()
            }).then(function(json: any) {
                if(cancelled) return;
                setAllJobs(json.searchResults || []);
                setStatus('ready');
            }).catch(function() {
                if(cancelled) return;
                setStatus('error');
            });
        return () => { cancelled = true; };
    }, [attempt]);

    // A new filter can leave the current page out of range, so start over
    useEffect(() => setPageIndex(0), [teamIndex]);

    // Keep the top of the list in view when paging so users don't land at the bottom of the next page
    const goToPage = (index:number) => {
        setPageIndex(index);
        listTopRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
    };

    const pageJobs = jobs.slice(startIndex, endIndex);

    return (
        <div className="relative pt-8 md:pt-16 px-4 md:px-3/25 pb-7 md:pb-14">
            {
                items &&
                <div className="absolute left-0 -top-25 w-full px-4 cursor-pointer">
                    <DropDown
                        options={teamOptions}
                        selectedOptionIndex={teamIndex}
                        expand={expandDropDown}
                        onExpandAction={() => setExpandDropDown(!expandDropDown)}
                        onSelectAction={(index) => {
                            setTeamIndex(index);
                            setExpandDropDown(false);
                        }}
                    />
                </div>
            }
            <div ref={listTopRef} className="scroll-mt-24" />
            {
                status === 'loading' &&
                <div aria-busy="true" aria-live="polite">
                    <span className="sr-only">Loading jobs</span>
                    {
                        [0, 1, 2].map((i) =>
                            <div
                                className="mb-2 border border-solid border-gcc-dark border-opacity-20 rounded-lg py-4 px-4 md:px-8 animate-pulse"
                                key={`skeleton-${i}`}
                            >
                                <div className="h-5 md:h-6 w-2/3 rounded bg-gcc-dark bg-opacity-10" />
                                <div className="mt-3 h-4 w-full rounded bg-gcc-dark bg-opacity-10" />
                                <div className="mt-2 h-4 w-5/6 rounded bg-gcc-dark bg-opacity-10" />
                            </div>
                        )
                    }
                </div>
            }
            {
                status === 'error' &&
                <div className="py-10 text-center" role="alert">
                    <p className="text-base md:text-lg">We couldn't load job openings right now.</p>
                    <button
                        className="mt-4 px-6 py-2 rounded border border-gcc-bright-orange text-gcc-bright-orange hover:bg-gcc-bright-orange hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gcc-bright-orange"
                        onClick={() => setAttempt(attempt + 1)}
                    >
                        Try again
                    </button>
                </div>
            }
            {
                status === 'ready' && jobs.length === 0 &&
                <div className="py-10 text-center" role="status">
                    <p className="text-base md:text-lg">
                        {teamIndex === -1 ? 'There are no open positions right now.' : 'No open positions match this team.'}
                    </p>
                    {
                        teamIndex !== -1 &&
                        <button
                            className="mt-4 underline text-gcc-bright-orange focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gcc-bright-orange"
                            onClick={() => setTeamIndex(-1)}
                        >
                            Show all teams
                        </button>
                    }
                </div>
            }
            {
                status === 'ready' && jobs.length > 0 &&
                <>
                    <p className="mb-3 text-sm md:text-base" role="status">
                        {`Showing ${startIndex + 1}-${startIndex + pageJobs.length} of ${jobs.length} open positions`}
                    </p>
                    {
                        pageJobs.map(({job, summary}:any) => {

                            const details = getJobDetails(job);
                            const postedDate = formatPostedDate(job.open_date);

                            return (
                                <a
                                    className="group mb-2 border border-solid border-gcc-dark border-opacity-20 rounded-lg py-3 md:py-5 px-4 md:px-8 flex items-center justify-between cursor-pointer transition-colors hover:border-gcc-bright-orange focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gcc-bright-orange"
                                    href={job.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    key={job.id || job.url}
                                >
                                    <div className="min-w-0">
                                        <h2 className="text-base md:text-xl2 font-extrabold leading-6 group-hover:text-gcc-bright-orange">{job.title}</h2>
                                        {
                                            details.length > 0 &&
                                            <ul className="mt-1 flex flex-wrap items-center text-sm md:text-base text-gcc-dark-gray">
                                                {
                                                    details.map((detail, index) =>
                                                        <li className="flex items-center" key={`detail-${index}`}>
                                                            {index > 0 && <span className="mx-2" aria-hidden="true">&bull;</span>}
                                                            <span className={index === 0 ? 'font-bold text-gcc-bright-orange' : ''}>{detail}</span>
                                                        </li>
                                                    )
                                                }
                                            </ul>
                                        }
                                        {
                                            summary?.job_summary &&
                                            <p
                                                className="mt-2 text-base md:text-lg"
                                                style={{
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 3,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                {summary.job_summary}
                                            </p>
                                        }
                                        {
                                            postedDate &&
                                            <p className="mt-2 text-sm text-gcc-gray">{postedDate}</p>
                                        }
                                        <span className="sr-only">(opens in a new tab)</span>
                                    </div>
                                    <img
                                        className="ml-4 w-3 h-6 flex-shrink-0 transition-transform group-hover:translate-x-1"
                                        src='/images/arrow-right-orange.svg'
                                        alt=''
                                        aria-hidden="true"
                                    />
                                </a>
                            );

                        })
                    }
                </>
            }
            {
                // If the amount of pages is greater than 1 show the pagination
                // Remember that pageIndex is 0 not 1
                status === 'ready' && numPages > 1?
                    <PageSelector
                        pageNum={pageIndex + 1}
                        numPages={numPages}
                        onPrevAction={() => goToPage(pageIndex === 0 ? 0 : pageIndex - 1)} // Don't go below 1
                        onNextAction={() => goToPage((pageIndex + 1) === numPages ? numPages - 1 : pageIndex + 1)} // Don't go above the total amount of pages
                    /> :
                    null
            }
        </div>
    );

}

export default JobListSlice;
